import { useNavigate } from "react-router-dom";
import galleryImages from "../data/gallery.json";
import { useScrollReveal } from "../hooks/useScrollReveal";

const Gallery = () => {
  const navigate = useNavigate();

  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({
    threshold: 0.3,
  });

  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal({
    threshold: 0.15,
  });

  const gotoGallary = () => {
    navigate("/gallary");
  };

  return (
    <section className="relative overflow-hidden py-16 md:py-24 bg-[var(--section-alt-bg)]">
      {/* Background Decorations */}
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full bg-orange-500/5 blur-[120px]" />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-orange-400/10 blur-[120px]" />

      <div className="max-w-7xl mx-auto px-5 relative z-10">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10"
          }`}
        >
          <span className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 font-semibold uppercase tracking-widest text-[10px] sm:text-xs">
            <span className="w-2 h-2 rounded-full bg-orange-500" />
            Our Gallery
          </span>

          <h2 className="mt-5 sm:mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] gowun-batang">
            Moments of Excellence
          </h2>

          <p className="mt-3 sm:mt-5 max-w-3xl mx-auto text-[var(--text-secondary)] text-sm sm:text-base md:text-lg leading-6 sm:leading-7 md:leading-8 px-2 sm:px-0">
            Explore memorable moments from our shooting competitions, training
            sessions, championship events, and achievements that reflect our
            passion for precision and excellence.
          </p>
        </div>

        {/* Gallery Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 md:gap-5"
        >
          {galleryImages.slice(0, 10).map((img: any, index: number) => (
            <div
              key={img.id}
              className={`relative h-56 sm:h-56 md:h-64 overflow-hidden rounded-2xl md:rounded-3xl group cursor-pointer
                transition-all duration-700
                hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-500/20
                ${
                  gridVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-10"
                }`}
              style={{
                transitionDelay: `${index * 90}ms`,
              }}
            >
              {/* Image */}
              <img
                src={img.imageUrl}
                alt={`Gallery ${img.id}`}
                loading="lazy"
                decoding="async"
                draggable={false}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Shine Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 overflow-hidden">
                <div className="absolute -left-24 top-0 h-full w-16 rotate-12 bg-white/20 blur-lg group-hover:left-[120%] transition-all duration-1000" />
              </div>

              {/* Border */}
              <div className="absolute inset-0 rounded-3xl border border-white/10 group-hover:border-white/30 transition-all duration-500" />

              {/* Image Number */}
              {/* <div className="absolute top-4 right-4">
                <div className="bg-black/50 backdrop-blur-md text-white text-xs font-semibold px-3 py-1 rounded-full">
                  {(index + 1).toString().padStart(2, "0")}
                </div>
              </div> */}

              {/* Bottom Content */}
              <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 md:p-5">
                <div className="translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                  {/* <h3 className="text-white font-semibold text-sm sm:text-base md:text-lg">
                    Shooting Event
                  </h3> */}

                  <p className="mt-1 text-sm text-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Excellence • Precision • Discipline
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={`mt-16 flex flex-col items-center transition-all duration-700 delay-500 ${
            gridVisible
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-[var(--text-secondary)] mb-6 text-center max-w-2xl">
            Discover more photos from our training sessions, tournaments, and
            special events in our complete gallery.
          </p>

          <button
            onClick={gotoGallary}
            className="group inline-flex items-center gap-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-orange-500/30"
          >
            View Full Gallery
            <svg
              className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
